import React from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { PrimitiveType, FormatXMLElementFn } from 'intl-messageformat';

/**
 * Shared rich value type used by both RichFormattedMessage and useRichIntl.
 */
export type RichValue =
  | PrimitiveType
  | React.ReactElement
  | FormatXMLElementFn<React.ReactNode, React.ReactNode>;

/**
 * Escapes a string for safe insertion into a RegExp constructor.
 */
export const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Escapes HTML entities for primitive interpolation.
 */
export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Identifies values that should be rendered as React nodes.
 */
export const isReactRichValue = (value: RichValue): boolean =>
  React.isValidElement(value) || typeof value === 'function';

/**
 * Normalizes XML-like placeholder syntax to ICU-like placeholders.
 *
 * Supported forms:
 * - <name />
 * - <name>...</name>
 */
export const normalizeTagPlaceholders = (
  message: string,
  key: string,
): string => {
  const escapedKey = escapeRegExp(key);

  return message
    .replace(new RegExp(`<${escapedKey}\\s*\\/>`, 'g'), `{${key}}`)
    .replace(
      new RegExp(`<${escapedKey}>[\\s\\S]*?<\\/${escapedKey}>`, 'g'),
      `{${key}}`,
    );
};

/**
 * Renders an interpolated placeholder value as a React node.
 */
export const renderRichValue = (
  value: RichValue,
  key: string,
): React.ReactNode => {
  if (React.isValidElement(value)) {
    return <React.Fragment key={key}>{value}</React.Fragment>;
  }

  if (typeof value === 'function') {
    const formatter = value as FormatXMLElementFn<
      React.ReactNode,
      React.ReactNode
    >;
    return <React.Fragment key={key}>{formatter([])}</React.Fragment>;
  }

  return <React.Fragment key={key}>{String(value ?? '')}</React.Fragment>;
};

/**
 * Sanitized HTML segment used by the rich output builder.
 */
const SanitizedSegment = ({ html }: { html: string }) => {
  const sanitized = DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['target'],
  });

  if (!sanitized) {
    return null;
  }

  return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
};

/**
 * Shared preprocessing output used by both component and hook.
 */
export interface PreparedRichMessage {
  /**
   * Final message string after preprocessing.
   *
   * This includes:
   * - XML-like placeholder normalization (e.g. <br /> -> {br})
   * - Primitive placeholder interpolation with HTML escaping
   *
   * It does NOT yet include React node injection; that happens later in
   * `buildRichOutput` for keys listed in `richKeys`.
   */
  processedMessage: string;
  /**
   * Placeholder keys that must be rendered as React nodes/components.
   *
   * Example:
   * values = {
   *   br: <br />,
   *   privacy_link: <PrivacyPolicyLink inline />,
   *   count: 3,
   * }
   *
   * -> richKeys = ['br', 'privacy_link']
   *
   * `count` is intentionally excluded because it is a primitive and should be
   * interpolated as escaped text, not rendered as a React node.
   */
  richKeys: string[];

  /**
   * Complete placeholder value map used during rich rendering.
   *
   * This is the merged object of:
   * - shared/default values (e.g. RICH_TAGS), and
   * - call-site values passed by the consumer.
   *
   * Call-site values override shared values for duplicate keys.
   */
  mergedValues: Record<string, RichValue>;
}

/**
 * Shared preprocessing phase:
 * 1) merge default tags + caller values
 * 2) normalize XML-style placeholders
 * 3) interpolate primitive placeholders (escaped)
 */
export const prepareRichMessage = ({
  baseMessage,
  values,
  sharedValues,
}: {
  baseMessage: string;
  values?: Record<string, RichValue>;
  sharedValues?: Record<string, RichValue>;
}): PreparedRichMessage => {
  const mergedValues = { ...(sharedValues || {}), ...(values || {}) };

  /**
   * `richKeys` are the placeholder names that correspond to React-rich values
   * (React elements or formatter functions).
   *
   * They are used later by `buildRichOutput` to:
   * 1) build a regex that matches only rich placeholders, and
   * 2) inject components into the final output at the correct positions.
   */
  const richKeys = Object.entries(values || {})
    .filter(([, value]) => isReactRichValue(value))
    .map(([key]) => key);

  let processedMessage = baseMessage;
  richKeys.forEach((key) => {
    processedMessage = normalizeTagPlaceholders(processedMessage, key);
  });

  const primitiveValues = Object.entries(values || {}).filter(
    ([, value]) => !isReactRichValue(value),
  );

  primitiveValues.forEach(([key, value]) => {
    const stringValue = escapeHtml(String(value ?? ''));
    processedMessage = processedMessage.replace(
      new RegExp(`\\{${escapeRegExp(key)}\\}`, 'g'),
      stringValue,
    );
  });

  return {
    processedMessage,
    richKeys,
    mergedValues,
  };
};

/**
 * Shared rendering phase that builds mixed output:
 * [sanitized-html-segment, rich-node, sanitized-html-segment, ...]
 */
export const buildRichOutput = ({
  processedMessage,
  richKeys,
  mergedValues,
}: PreparedRichMessage): React.ReactNode[] => {
  // Match only placeholders that map to rich/component values.
  // Primitive placeholders were already interpolated in `prepareRichMessage`.
  const richMatcher = new RegExp(
    `\\{(${richKeys.map(escapeRegExp).join('|')})\\}`,
    'g',
  );

  const outputParts: React.ReactNode[] = [];
  let cursor = 0;
  let match = richMatcher.exec(processedMessage);

  while (match) {
    const matchedPlaceholder = match[0];
    const matchedKey = match[1];
    const startIndex = match.index;

    if (startIndex > cursor) {
      outputParts.push(
        <SanitizedSegment
          key={`segment-${cursor}`}
          html={processedMessage.slice(cursor, startIndex)}
        />,
      );
    }

    const richValue = mergedValues[matchedKey];

    if (richValue !== undefined) {
      outputParts.push(
        renderRichValue(richValue, `value-${matchedKey}-${startIndex}`),
      );
    } else {
      outputParts.push(
        <SanitizedSegment
          key={`unknown-${startIndex}`}
          html={escapeHtml(matchedPlaceholder)}
        />,
      );
    }

    cursor = startIndex + matchedPlaceholder.length;
    match = richMatcher.exec(processedMessage);
  }

  if (cursor < processedMessage.length) {
    outputParts.push(
      <SanitizedSegment
        key={`segment-last-${cursor}`}
        html={processedMessage.slice(cursor)}
      />,
    );
  }

  return outputParts;
};
